/* eslint-disable arrow-body-style */
import {Actions, createEffect, ofType} from "@ngrx/effects";
import {inject} from "@angular/core";
import {catchError, exhaustMap, filter, forkJoin, map, of, switchMap} from "rxjs";
import {FundService} from "../../services/fund.service";
import {
    CopyFundReportAndValuationsActions,
    FundActions,
    FundCreateActions,
    FundDetailActions,
    FundListActions,
    FundNameActions,
    FundOverviewActions
} from "../fund.actions";
import {AssetValuationWriteoffActions} from "../../../asset/store/asset.actions";
import {Store} from "@ngrx/store";
import {selectFundContext, selectFundOverviewParams, selectSelectedFund} from "./fund.selectors";
import {EMPTY_FUND, Fund} from "../../models/fund";
import {DecimalFormatPipe} from "../../../shared/pipes/decimal-format/decimal-format.pipe";
import {sourceIfChanged} from "../../../shared/model/traceable";
import {selectSelectedSourceDataset} from "../../../import/store/import.selectors";
import {DatasetActions} from "../../../import/store/import.actions";
import {NotificationActions} from "../../../notification/store/notification.actions";
import {tap} from "rxjs/operators";
import {Router} from "@angular/router";
import {ErrorHandlingUtil} from "../../../shared/utils/error-handling-util";
import {concatLatestFrom} from "@ngrx/operators";
import {MatSnackBar} from "@angular/material/snack-bar";
import {summarizeBulkResults, toBulkResult} from "../../../shared/utils/bulk-operation-util";


// #########################################
// Fund OVERVIEW EFFECTS (server-side pagination)
// #########################################

export const triggerFundSearchOnParamsChange = createEffect(
    (actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(
                FundOverviewActions.changePage,
                FundOverviewActions.changeSort,
                FundOverviewActions.changeSearch,
                FundOverviewActions.changeArchived
            ),
            map(() => FundOverviewActions.search())
        );
    },
    {functional: true}
);

export const searchFunds = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), fundService = inject(FundService)) => {
        return actions$.pipe(
            ofType(FundOverviewActions.search),
            concatLatestFrom(() => [
                store$.select(selectFundOverviewParams),
                store$.select(selectSelectedSourceDataset)
            ]),
            switchMap(([, params, dataset]) =>
                fundService.searchFunds(dataset, params).pipe(
                    map((result) => FundOverviewActions.searchSuccess({result})),
                    catchError((errorResponse: any) => of(FundOverviewActions.searchError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                )
            )
        );
    },
    {functional: true}
);

export const archiveOverviewFunds = createEffect(
    (actions$ = inject(Actions), fundService = inject(FundService)) => {
        return actions$.pipe(
            ofType(FundOverviewActions.archiveFunds),
            exhaustMap((action) => {
                const fundsToUpdate = action.funds.filter((fund) => fund.id !== undefined);
                if (fundsToUpdate.length === 0) {
                    return of(FundOverviewActions.archiveFundsError({errorMsg: "No funds selected"}));
                }
                return forkJoin(
                    fundsToUpdate.map((fund) => toBulkResult(fundService.archiveFund(fund.id, action.archived)))
                ).pipe(
                    map((results) => {
                        const {count, failed, firstError} = summarizeBulkResults(results);
                        return count === 0
                            ? FundOverviewActions.archiveFundsError({errorMsg: firstError ?? "Funds could not be archived."})
                            : FundOverviewActions.archiveFundsSuccess({count, failed, archived: action.archived});
                    })
                );
            })
        );
    },
    {functional: true}
);

export const reloadFundsAfterArchive = createEffect(
    (actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(FundOverviewActions.archiveFundsSuccess),
            map(() => FundOverviewActions.search())
        );
    },
    {functional: true}
);

export const showArchiveFundsSuccess = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(FundOverviewActions.archiveFundsSuccess),
            tap((action) => {
                const noun = action.count === 1 ? "fund" : "funds";
                const verb = action.archived ? "archived" : "unarchived";
                const failedSuffix = action.failed > 0 ? ` ${action.failed} failed.` : "";
                snackBar.open(`${action.count} ${noun} ${verb}.${failedSuffix}`, "", {duration: 3000});
            })
        );
    },
    {functional: true, dispatch: false}
);

export const showArchiveFundsError = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(FundOverviewActions.archiveFundsError),
            tap((action) => {
                snackBar.open("Funds could not be archived. " + action.errorMsg, "", {duration: 3000});
            })
        );
    },
    {functional: true, dispatch: false}
);


// #########################################
// Fund LIST EFFECTS
// #########################################

export const initFundOverview = createEffect(
    (actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(FundListActions.open),
            map(() => FundListActions.load({archived: false}))
        );
    },
    {functional: true}
);

export const loadFundList = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), fundService = inject(FundService)) => {
        return actions$.pipe(
            ofType(FundListActions.load),
            concatLatestFrom(() => store$.select(selectSelectedSourceDataset)),
            exhaustMap(([action, dataset]) =>
                fundService.getFunds(dataset, action.archived).pipe(
                    map((funds) => FundListActions.loadSuccess({funds})),
                    catchError((errorResponse: any) => of(FundListActions.loadError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                )
            )
        );
    },
    {functional: true}
);

// #########################################
// CREATE FUND
// #########################################

export const createFund = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), fundService = inject(FundService)) => {
        return actions$.pipe(
            ofType(FundCreateActions.create),
            concatLatestFrom(() => store$.select(selectSelectedSourceDataset)),
            exhaustMap(([action, dataset]) => {
                return fundService.createFund({
                    ...EMPTY_FUND,
                    ...{
                        name: {
                            ...EMPTY_FUND.name,
                            text: action.fundName
                        },
                        isArchived: {
                            ...EMPTY_FUND.isArchived,
                            value: false
                        }
                    }
                }, dataset).pipe(
                    map((fund) => FundCreateActions.createSuccess({fund})),
                    catchError((errorResponse: any) => of(FundCreateActions.createError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                );
            })
        );
    },
    {functional: true}
);

export const navigateToFundDetailsPageAfterCreate = createEffect(
    (actions$ = inject(Actions), router$ = inject(Router)) => {
        return actions$.pipe(
            ofType(FundCreateActions.createSuccess),
            tap((action) => router$.navigate(["/funds", action.fund.id])));
    },
    {functional: true, dispatch: false}
);

export const showCreateError = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(FundCreateActions.createError),
            tap((action) => {
                snackBar.open("Fund could not be created. " + action.errorMsg, "", {
                    duration: 3000
                });
            })
        );
    },
    {functional: true, dispatch: false}
);

// #########################################
// FUND Data
// #########################################

export const loadFundData = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), fundService = inject(FundService)) => {
        return actions$.pipe(
            ofType(FundActions.loadFundData),
            concatLatestFrom(() => store$.select(selectSelectedSourceDataset)),
            exhaustMap(([action, dataset]) =>
                fundService.getFundData(action.fundId, action.fundReportId, action.fundValuationId, action.dealId, dataset).pipe(
                    map((fundData) => FundActions.loadFundDataSuccess({fundData})),
                    catchError((errorResponse: any) => of(FundActions.loadFundDataError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                )
            )
        );
    },
    {functional: true}
);

// US-1189: after the user confirms an asset as a write-off from the partnership-investments table, reload the
// fund so the row's warning icon and the subtitle banner count drop. The confirmation is stored on the asset
// valuation, so only a fund-data reload brings the recomputed records back.
export const reloadFundAfterWriteoffConfirmation = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(AssetValuationWriteoffActions.confirmSuccess),
            concatLatestFrom(() => store$.select(selectFundContext)),
            filter(([, context]) => context.fundId != null),
            map(([, context]) => FundActions.loadFundData({
                fundId: context.fundId as number,
                fundReportId: context.fundReportId,
                fundValuationId: context.fundValuationId,
                dealId: context.dealId
            }))
        );
    },
    {functional: true}
);

// When the merge target set changes (which clears all merge targets server-side), reload the current fund
// so the "Merge Targets" sidenav reflects the cleared/updated state. Gated to a real (non-master) source so
// the master-resetting flows (closeMergePage / startMerge / post-merge) don't trigger a needless reload.
// Crucially, only reload when we're actually ON the in-context fund's page: navigation flows
// (navigateToImportedFund) dispatch setsourceandtarget *before* routing, so the fund context still holds the
// previously viewed fund at that instant — reloading it here would fetch the wrong fund (against the new
// dataset) and, via loadFundData's exhaustMap, could drop the correct post-navigation load. Matching the
// current route's fundId against the in-context fundId restricts this to the header target-set change, where
// the user stays on the fund page.
export const reloadFundAfterTargetChange = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), router$ = inject(Router)) => {
        return actions$.pipe(
            ofType(DatasetActions.setsourceandtarget),
            filter((action) => action.source !== "master" && action.target != null),
            concatLatestFrom(() => store$.select(selectFundContext)),
            filter(([, context]) => context.fundId != null && isOnFundRoute(router$.url, context.fundId)),
            map(([, context]) => FundActions.loadFundData({
                fundId: context.fundId as number,
                fundReportId: context.fundReportId,
                fundValuationId: context.fundValuationId,
                dealId: context.dealId
            }))
        );
    },
    {functional: true}
);

// True when the active route is the fund-details page (funds/:fundId) for the given fund. Used to avoid
// reloading a stale fund when setsourceandtarget is dispatched just before navigating to a different fund.
function isOnFundRoute(url: string, fundId: number): boolean {
    // url is e.g. "/funds/123" or "/funds/123/valuation/sensitivity" (possibly with query/fragment)
    const fundSegment = url.split(/[?#]/)[0].split("/")[2];
    return fundSegment === String(fundId);
}

// #########################################
// FUND Name
// #########################################
export const saveFundName = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), fundService = inject(FundService)) => {
        return actions$.pipe(
            ofType(FundNameActions.save),
            concatLatestFrom(() => store$.select(selectFundContext)),
            exhaustMap(([action, ctx]) => {
                if (ctx.fundId) {
                    return fundService.updateFundName(
                        ctx.fundId,
                        action.fundName
                    ).pipe(
                        map((fund) => FundNameActions.saveSuccess({fund})),
                        catchError((errorResponse: any) => of(FundNameActions.saveError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                    );
                } else {
                    return of(FundNameActions.saveError({errorMsg: "Fund could not be found"}));
                }
            })
        );
    },
    {functional: true}
);

export const showSaveFundNameSuccess = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(FundNameActions.saveSuccess),
            tap(() => {
                snackBar.open("Fund Name successfully updated.", "", {
                    duration: 3000
                });
            })
        );
    },
    {functional: true, dispatch: false}
);

// #########################################
// FUND Archive
// #########################################
export const archiveFund = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), fundService = inject(FundService)) => {
        return actions$.pipe(
            ofType(FundActions.archive),
            concatLatestFrom(() => store$.select(selectFundContext)),
            exhaustMap(([action, ctx]) => {
                if (ctx.fundId) {
                    return fundService.archiveFund(
                        ctx.fundId,
                        action.archived
                    ).pipe(
                        map(fund => FundActions.archiveSuccess({fund})),
                        catchError((errorResponse: any) => of(FundActions.archiveError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                    );
                } else {
                    return of(FundActions.archiveError({errorMsg: "Fund could not be found"}));
                }
            })
        );
    },
    {functional: true}
);


export const showArchiveFundSuccess = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(FundActions.archiveSuccess),
            tap(() => {
                snackBar.open("Fund successfully archived.", "", {
                    duration: 3000
                });
            })
        );
    },
    {functional: true, dispatch: false}
);

// #########################################
// FUND Details
// #########################################

export const saveFundDetails = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), fundService = inject(FundService)) => {
        return actions$.pipe(
            ofType(FundDetailActions.save),
            concatLatestFrom(() => [
                store$.select(selectSelectedFund),
                store$.select(selectFundContext),
                store$.select(selectSelectedSourceDataset)
            ]),
            exhaustMap(([action, state, fundContext, dataset]) => {
                const selectedFund: Fund = state.fund;

                // Settings-fillable terms: compute the new value once, then flip source to "user" only
                // when it actually changed (see stampSourceIfChanged), so the "applied from user settings"
                // gear disappears when the user overrides a defaulted value but survives an unrelated save.
                const managementFeeFraction = DecimalFormatPipe.transformPercentToFraction(action.managementFee);
                const carryFraction = DecimalFormatPipe.transformPercentToFraction(action.carry);
                const hurdleRateFraction = DecimalFormatPipe.transformPercentToFraction(action.hurdleRate);
                const secondaryManagementFeeFraction = DecimalFormatPipe.transformPercentToFraction(action.secondaryManagementFee);
                const secondaryCarryFraction = DecimalFormatPipe.transformPercentToFraction(action.secondaryCarry);
                const secondaryHurdleFraction = DecimalFormatPipe.transformPercentToFraction(action.secondaryHurdleRate);

                const fund = {
                    ...selectedFund,
                    ...{
                        vintageYear: {
                            ...selectedFund.vintageYear,
                            date: action.vintageYear
                        },
                        purchaseYear: {
                            ...selectedFund.purchaseYear,
                            date: action.purchaseYear
                        },
                        endYear: {
                            ...selectedFund.endYear,
                            date: action.endYear,
                            source: sourceIfChanged(action.endYear !== selectedFund.endYear.date, selectedFund.endYear.source)
                        },
                        managementFee: {
                            ...selectedFund.managementFee,
                            fraction: managementFeeFraction,
                            source: sourceIfChanged(managementFeeFraction !== selectedFund.managementFee.fraction, selectedFund.managementFee.source)
                        },
                        carry: {
                            ...selectedFund.carry,
                            fraction: carryFraction,
                            source: sourceIfChanged(carryFraction !== selectedFund.carry.fraction, selectedFund.carry.source)
                        },
                        hurdleRate: {
                            ...selectedFund.hurdleRate,
                            fraction: hurdleRateFraction,
                            source: sourceIfChanged(hurdleRateFraction !== selectedFund.hurdleRate.fraction, selectedFund.hurdleRate.source)
                        },
                        secondaryManagementFeeRate: {
                            ...selectedFund.secondaryManagementFeeRate,
                            fraction: secondaryManagementFeeFraction,
                            source: sourceIfChanged(
                                secondaryManagementFeeFraction !== selectedFund.secondaryManagementFeeRate.fraction,
                                selectedFund.secondaryManagementFeeRate.source)
                        },
                        secondaryCarryRate: {
                            ...selectedFund.secondaryCarryRate,
                            fraction: secondaryCarryFraction,
                            source: sourceIfChanged(secondaryCarryFraction !== selectedFund.secondaryCarryRate.fraction, selectedFund.secondaryCarryRate.source)
                        },
                        secondaryHurdleRate: {
                            ...selectedFund.secondaryHurdleRate,
                            fraction: secondaryHurdleFraction,
                            source: sourceIfChanged(secondaryHurdleFraction !== selectedFund.secondaryHurdleRate.fraction, selectedFund.secondaryHurdleRate.source)
                        },
                        stage: {
                            ...selectedFund.stage,
                            code: action.stage
                        },
                        managementFeeCalculationMethod: {
                            ...selectedFund.managementFeeCalculationMethod,
                            code: action.managementFeeCalculation,
                            source: sourceIfChanged(
                                action.managementFeeCalculation !== selectedFund.managementFeeCalculationMethod.code,
                                selectedFund.managementFeeCalculationMethod.source)
                        },
                        regions: action.regions,
                        currencyIso: {
                            ...selectedFund.currencyIso,
                            code: action.currencyIso
                        },
                        country: {
                            ...selectedFund.country,
                            code: action.country ?? undefined
                        },
                        size: {
                            ...selectedFund.size,
                            amount: DecimalFormatPipe.transformToMillionsNum(action.size)
                        },
                        mainFundSize: {
                            ...selectedFund.mainFundSize,
                            amount: DecimalFormatPipe.transformToMillionsNum(action.mainFundSize)
                        }
                    }
                };

                if (selectedFund.id) {
                    const fundId = selectedFund.id;
                    return fundService.updateFund(
                        fundId,
                        fund,
                        fundContext.fundReportId,
                        fundContext.fundValuationId,
                        fundContext.dealId,
                        dataset
                    ).pipe(
                        map((fundData) => FundDetailActions.saveSuccess({fundData})),
                        catchError((errorResponse: any) => of(FundDetailActions.saveError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                    );
                } else {
                    return of(FundDetailActions.saveError({errorMsg: "Fund could not be found"}));
                }
            })
        );
    },
    {functional: true}
);

export const showSaveSuccess = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(FundDetailActions.saveSuccess),
            tap(() => {
                snackBar.open("Fund successfully updated.", "", {
                    duration: 3000
                });
            })
        );
    },
    {functional: true, dispatch: false}
);

// #########################################
// FUND MERGE AND BASELINE
// #########################################

export const setMergeTargetId = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), fundService = inject(FundService)) => {
        return actions$.pipe(
            ofType(FundActions.setMergeTarget),
            concatLatestFrom(() => [store$.select(selectFundContext), store$.select(selectSelectedSourceDataset)]),
            exhaustMap(([action, fundContext, dataset]) => {
                if (fundContext.fundId) {
                    return fundService.setMergeTarget(fundContext.fundId, fundContext.fundReportId, action.mergeTargetId, dataset).pipe(
                        map((fundData) => FundActions.setMergeTargetSuccess({fundData})),
                        catchError((errorResponse: any) => of(FundActions.setMergeTargetError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                    );
                } else {
                    return of(FundActions.setMergeTargetError({errorMsg: "Fund could not be found"}));
                }
            })
        );
    },
    {functional: true}
);

export const loadMergePreview = createEffect(
    (actions$ = inject(Actions), fundService = inject(FundService), store$ = inject(Store)) => {
        return actions$.pipe(
            ofType(FundActions.loadMergePreview),
            concatLatestFrom(() => [store$.select(selectFundContext), store$.select(selectSelectedSourceDataset)]),
            switchMap(([, fundContext, dataset]) => {
                if (fundContext.fundId) {
                    return fundService.getMergePreview(fundContext.fundId, dataset).pipe(
                        map((preview) => FundActions.loadMergePreviewSuccess({preview})),
                        catchError((errorResponse: any) => of(FundActions.loadMergePreviewError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                    );
                } else {
                    return of(FundActions.loadMergePreviewError({errorMsg: "Fund could not be found"}));
                }
            })
        );
    },
    {functional: true}
);

// #########################################
// FUND COPY VALUATION AND REPORT
// #########################################

export const copyFundReportAndValuations = createEffect(
    (actions$ = inject(Actions), fundService = inject(FundService)) => {
        return actions$.pipe(
            ofType(CopyFundReportAndValuationsActions.copy),
            // The copy now runs asynchronously: the request returns a taskInfoId immediately and the user
            // stays on the current page. No navigation — the completion notification's deep link takes them
            // to the new copy. trackCopyJob (below) speeds up notification polling until it arrives.
            exhaustMap((action) => {
                return fundService.copyFundReportAndValuations(action.copyConfig).pipe(
                    map((taskInfoId) => CopyFundReportAndValuationsActions.copySuccess({taskInfoId})),
                    catchError((errorResponse: any) => of(CopyFundReportAndValuationsActions.copyError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                );
            })
        );
    },
    {functional: true}
);

export const trackCopyJob = createEffect(
    (actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(CopyFundReportAndValuationsActions.copySuccess),
            map(({taskInfoId}) => NotificationActions.trackJob({taskInfoId}))
        );
    },
    {functional: true}
);

export const clearCopyConfigOnCopySuccess = createEffect(
    (actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(CopyFundReportAndValuationsActions.copySuccess),
            map(() => CopyFundReportAndValuationsActions.clearCopyConfigAndWorkflow()));
    },
    {functional: true}
);

export const showCopySuccess = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(CopyFundReportAndValuationsActions.copySuccess),
            tap(() => {
                snackBar.open("Copy started — you'll be notified when it's ready.", "OK", {
                    duration: 4000
                });
            })
        );
    },
    {functional: true, dispatch: false}
);

export const showCopyError = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(CopyFundReportAndValuationsActions.copyError),
            tap((action) => {
                snackBar.open("Copy failed. " + action.errorMsg, "", {
                    duration: 5000
                });
            })
        );
    },
    {functional: true, dispatch: false}
);
